import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, FileText } from "lucide-react";
import api from "../api/client";
import { useAuth } from "../contexts/AuthContext";

/**
 * Lightweight blocking modal asking a team member to re-accept the Terms +
 * Privacy docs after the admin has materially updated them. Distinct from
 * ChangePasswordModal — no password fields, just the acknowledgement.
 */
export default function AcceptTermsModal() {
  const { refresh, logout } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      setErr("Please tick the box to confirm.");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      await api.post("/auth/accept-terms");
      toast.success("Thanks — your acceptance is on file.");
      await refresh();
    } catch {
      setErr("Could not record acceptance. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4"
      data-testid="accept-terms-modal"
    >
      <form
        onSubmit={submit}
        className="bg-white border border-gray-300 max-w-md w-full p-7 rounded-sm shadow-2xl"
      >
        <div className="flex items-start gap-3 mb-5">
          <div className="bg-[#002FA7] text-white p-2 rounded-sm">
            <FileText className="w-5 h-5" />
          </div>
          <div>
            <h4 className="font-black text-xl tracking-tight">Updated Terms</h4>
            <p className="text-xs text-gray-500 mt-1">
              We&apos;ve updated our Terms of Service and Privacy Policy. Please
              read and confirm to continue.
            </p>
          </div>
        </div>

        <div className="border-l-4 border-[#002FA7] bg-[#F0F4FF] px-3 py-3 mb-5 text-xs text-gray-700 space-y-1">
          <Link
            to="/terms"
            target="_blank"
            data-testid="at-terms-link"
            className="block text-[#002FA7] hover:underline font-semibold"
          >
            Read the Terms of Service →
          </Link>
          <Link
            to="/privacy"
            target="_blank"
            data-testid="at-privacy-link"
            className="block text-[#002FA7] hover:underline font-semibold"
          >
            Read the Privacy Policy →
          </Link>
        </div>

        <label className="flex items-start gap-2 mb-4 cursor-pointer text-xs text-gray-800">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            data-testid="at-accept-checkbox"
            className="mt-0.5"
          />
          <span>
            I have read and agree to the updated Terms of Service and
            Privacy Policy.
          </span>
        </label>

        {err && (
          <div
            className="mb-4 border-l-4 border-red-600 bg-red-50 px-3 py-2 text-xs text-red-900"
            data-testid="at-error"
          >
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="at-submit"
          className="w-full bg-[#002FA7] text-white py-2.5 font-bold rounded-sm hover:bg-[#002277] disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          CONTINUE
        </button>

        <button
          type="button"
          onClick={logout}
          data-testid="at-logout"
          className="w-full mt-3 text-xs text-gray-500 hover:text-gray-900"
        >
          Sign out instead
        </button>
      </form>
    </div>
  );
}
